package com.trevorwiebe.timesheet.admin.customer.presentation

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Card
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.toMutableStateList
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.model.Customer
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.DialogButtonRow
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun CustomersScreen(
    viewModel: CustomersViewModel = koinViewModel(),
    onBack: () -> Unit
) {
    val state by viewModel.state.collectAsState()

    val filteredCustomers = run {
        val statusFilters = state.activeFilters
        val byStatus = if (statusFilters.isEmpty()) {
            state.customers.filter { !it.deleted }
        } else {
            state.customers.filter { customer ->
                (CustomerFilter.ACTIVE in statusFilters && !customer.deleted) ||
                (CustomerFilter.DELETED in statusFilters && customer.deleted)
            }
        }
        byStatus.sortedBy { it.contactName.lowercase() }
    }

    Scaffold(
        topBar = {
            TopBar(
                title = "Customers",
                backIcon = { BackIcon() },
                onBack = onBack
            )
        },
        floatingActionButton = {
            FloatingActionButton(
                onClick = { viewModel.onEvent(CustomersEvent.OnAddCustomerClick) },
                containerColor = MaterialTheme.colorScheme.tertiary,
                contentColor = MaterialTheme.colorScheme.onTertiary
            ) {
                Icon(Icons.Default.Add, contentDescription = "Add customer")
            }
        }
    ) { paddingValues ->
        ResponsiveContent(modifier = Modifier.padding(paddingValues)) {
        Column(
            modifier = Modifier
                .fillMaxSize()
        ) {
            CustomersFilterChipRow(
                activeFilters = state.activeFilters,
                onFilterToggled = { viewModel.onEvent(CustomersEvent.OnFilterToggled(it)) }
            )
            HorizontalDivider()
            if (state.isLoading) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = MaterialTheme.colorScheme.tertiary)
                }
            } else {
                LazyColumn(modifier = Modifier.fillMaxSize()) {
                    items(filteredCustomers) { customer ->
                        CustomerRow(
                            customer = customer,
                            onClick = { viewModel.onEvent(CustomersEvent.OnEditCustomerClick(customer)) }
                        )
                    }
                }
            }
        }

        if (state.showAddEditDialog) {
            AddEditCustomerDialog(
                editing = state.editingCustomer,
                onDismiss = { viewModel.onEvent(CustomersEvent.OnDismissDialog) },
                onSave = { contactName, companyName, address, phone, emails, paymentTerms, notes ->
                    viewModel.onEvent(
                        CustomersEvent.OnSaveCustomer(
                            contactName, companyName, address, phone, emails, paymentTerms, notes
                        )
                    )
                },
                onDelete = { viewModel.onEvent(CustomersEvent.OnDeleteCustomer) },
                onRestore = { viewModel.onEvent(CustomersEvent.OnRestoreCustomer) }
            )
        }

        NativeDialog(
            visible = state.error != null,
            title = "Something went wrong",
            message = state.error ?: "",
            confirmText = "OK",
            onConfirm = { viewModel.onEvent(CustomersEvent.OnErrorShown) },
            onDismiss = { viewModel.onEvent(CustomersEvent.OnErrorShown) }
        )
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun CustomersFilterChipRow(
    activeFilters: Set<CustomerFilter>,
    onFilterToggled: (CustomerFilter) -> Unit
) {
    FlowRow(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 4.dp),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalArrangement = Arrangement.spacedBy((-8).dp)
    ) {
        Text(
            text = "Filter",
            fontSize = 14.sp,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
            modifier = Modifier.align(Alignment.CenterVertically).padding(end = 4.dp)
        )
        CustomerFilter.entries.forEach { filter ->
            val selected = filter in activeFilters
            FilterChip(
                selected = selected,
                onClick = { onFilterToggled(filter) },
                label = { Text(filter.name.lowercase().replaceFirstChar { it.uppercase() }) },
                border = FilterChipDefaults.filterChipBorder(
                    enabled = true,
                    borderColor = MaterialTheme.colorScheme.tertiary,
                    selected = selected
                ),
                colors = FilterChipDefaults.filterChipColors(
                    containerColor = MaterialTheme.colorScheme.background,
                    selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                    selectedLabelColor = MaterialTheme.colorScheme.onTertiary
                )
            )
        }
    }
}

@Composable
private fun CustomerRow(
    customer: Customer,
    onClick: () -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(start = 16.dp, end = 16.dp, top = 12.dp, bottom = 12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = customer.contactName.ifEmpty { "(no name)" },
                    fontSize = 16.sp,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                val contactLine = (listOf(customer.companyName, customer.phone) + customer.emails)
                    .filter { it.isNotBlank() }
                    .joinToString(" · ")
                if (contactLine.isNotEmpty()) {
                    Text(
                        text = contactLine,
                        fontSize = 14.sp,
                        color = MaterialTheme.colorScheme.secondary,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
            if (customer.deleted) {
                Text(
                    text = "Deleted",
                    color = MaterialTheme.colorScheme.error,
                    fontSize = 14.sp,
                    maxLines = 1
                )
            }
        }
        HorizontalDivider()
    }
}

@Composable
private fun AddEditCustomerDialog(
    editing: Customer?,
    onDismiss: () -> Unit,
    onSave: (
        contactName: String,
        companyName: String,
        address: String,
        phone: String,
        emails: List<String>,
        paymentTerms: String,
        notes: String
    ) -> Unit,
    onDelete: () -> Unit,
    onRestore: () -> Unit
) {
    var contactName by remember { mutableStateOf(editing?.contactName ?: "") }
    var companyName by remember { mutableStateOf(editing?.companyName ?: "") }
    var address by remember { mutableStateOf(editing?.address ?: "") }
    var phone by remember { mutableStateOf(editing?.phone ?: "") }
    val emails = remember { (editing?.emails?.ifEmpty { null } ?: listOf("")).toMutableStateList() }
    var paymentTerms by remember { mutableStateOf(editing?.paymentTerms ?: "") }
    var notes by remember { mutableStateOf(editing?.notes ?: "") }
    var showDeleteConfirm by remember { mutableStateOf(false) }
    var validationError by remember { mutableStateOf<String?>(null) }

    NativeDialog(
        visible = showDeleteConfirm,
        title = "Delete Customer",
        message = "Are you sure you want to delete this customer? It can be restored, and existing records will keep their reference.",
        confirmText = "Delete",
        onConfirm = onDelete,
        dismissText = "Cancel",
        onDismiss = { showDeleteConfirm = false }
    )

    Dialog(onDismissRequest = onDismiss) {
        Card(
            modifier = Modifier.responsiveDialogWidth(),
            shape = RoundedCornerShape(16.dp)
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
                    .padding(16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    text = if (editing == null) "Add Customer" else "Edit Customer",
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.height(16.dp))

                TimesheetTextField(
                    text = contactName,
                    label = "Contact Name",
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                    onTextChange = { contactName = it }
                )
                Spacer(modifier = Modifier.height(8.dp))

                TimesheetTextField(
                    text = companyName,
                    label = "Company Name",
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                    onTextChange = { companyName = it }
                )
                Spacer(modifier = Modifier.height(8.dp))

                TimesheetTextField(
                    modifier = Modifier.height(120.dp),
                    text = address,
                    label = "Address",
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                    onTextChange = { address = it }
                )
                Spacer(modifier = Modifier.height(8.dp))

                TimesheetTextField(
                    text = phone,
                    label = "Phone",
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                    onTextChange = { phone = it }
                )
                Spacer(modifier = Modifier.height(8.dp))

                emails.forEachIndexed { index, email ->
                    TimesheetTextField(
                        text = email,
                        label = if (emails.size == 1) "Email" else "Email ${index + 1}",
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                        trailingIcon = if (emails.size > 1) {
                            {
                                IconButton(onClick = { emails.removeAt(index) }) {
                                    Icon(Icons.Default.Close, contentDescription = "Remove email")
                                }
                            }
                        } else null,
                        onTextChange = { emails[index] = it }
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                }
                TextButton(
                    onClick = { emails.add("") },
                    modifier = Modifier.align(Alignment.Start)
                ) {
                    Icon(Icons.Default.Add, contentDescription = null, tint = MaterialTheme.colorScheme.tertiary)
                    Text("Add email", color = MaterialTheme.colorScheme.tertiary)
                }
                Text(
                    text = "Invoices are emailed to every address.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
                    modifier = Modifier.fillMaxWidth().padding(start = 4.dp, bottom = 8.dp)
                )

                TimesheetTextField(
                    text = paymentTerms,
                    label = "Payment Terms",
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
                    onTextChange = { paymentTerms = it }
                )
                Spacer(modifier = Modifier.height(8.dp))

                TimesheetTextField(
                    modifier = Modifier.height(120.dp),
                    text = notes,
                    label = "Notes",
                    keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
                    onTextChange = { notes = it }
                )
                Spacer(modifier = Modifier.height(8.dp))

                validationError?.let { error ->
                    Text(
                        text = error,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(bottom = 8.dp)
                    )
                }

                val isDeleted = editing?.deleted == true
                DialogButtonRow(
                    confirmButtonText = "Save",
                    confirmEnabled = !isDeleted,
                    onConfirmClick = {
                        if (contactName.isBlank()) {
                            validationError = "Please enter Contact Name before saving."
                        } else if (phone.isNotBlank() && !Util.isValidUsPhone(phone.trim())) {
                            validationError = "Please enter a valid US phone number."
                        } else if (emails.any { it.isNotBlank() && !Util.isValidEmail(it.trim()) }) {
                            validationError = "Please enter a valid email address."
                        } else {
                            validationError = null
                            onSave(
                                contactName.trim(),
                                companyName.trim(),
                                address.trim(),
                                phone.trim(),
                                emails.map { it.trim() }.filter { it.isNotEmpty() },
                                paymentTerms.trim(),
                                notes.trim()
                            )
                        }
                    },
                    dismissButtonText = "Cancel",
                    onDismissClick = onDismiss,
                    neutralButtonText = when {
                        editing == null -> null
                        isDeleted -> "Restore"
                        else -> "Delete"
                    },
                    onNeutralClick = if (isDeleted) onRestore else { { showDeleteConfirm = true } }
                )
            }
        }
    }
}
